import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, Heart, ShoppingCart, Star } from "lucide-react";
import { addWishlistEntry, removeFromWishlist } from "../../../features/wishlistSlice";
import QuickViewModal from "./QuickViewModal";
import { formatMoney as fmtMoney, normalizeCurrencyRates } from "../../../util/money";
import { buildProductPath, buildProductRouteState } from "../../../util/productSlug";

const FALLBACK_IMAGE = "/placeholder.svg";
const MAX_SCRUB_FRAMES = 6;

const PRODUCT_TYPE_KEYWORDS = [
  { label: "Electronics", regex: /(phone|laptop|camera|headphone|earbud|speaker|monitor|keyboard|mouse|watch|charger|usb|smart)/i },
  { label: "Fashion", regex: /(shirt|dress|hoodie|jacket|sneaker|shoe|fashion|necklace|bracelet|ring|bag|wallet)/i },
  { label: "Home & Living", regex: /(kitchen|sofa|chair|table|home|pillow|bed|lamp|storage|clean|vacuum|decor)/i },
  { label: "Beauty", regex: /(beauty|makeup|lipstick|skincare|serum|cream|perfume|hair|trimmer)/i },
  { label: "Sports", regex: /(fitness|gym|cycle|bike|running|sport|yoga|outdoor|camp|football|basketball)/i },
  { label: "Toys", regex: /(toy|kids|baby|doll|puzzle|lego|game)/i },
];

const toNumber = (...values) => {
  for (const raw of values) {
    if (raw == null) continue;
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    const cleaned = String(raw).replace(/,/g, "").replace(/[^\d.+-]/g, "").trim();
    if (!cleaned) continue;
    const parsed = Number(cleaned);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const pickPreferredPrice = (...values) => {
  let fallbackZero = null;
  for (const raw of values) {
    const parsed = toNumber(raw);
    if (parsed == null) continue;
    if (parsed > 0) return parsed;
    if (fallbackZero == null) fallbackZero = parsed;
  }
  return fallbackZero;
};

const uniqueList = (values) => Array.from(new Set(values.filter(Boolean)));

const normalizeId = (value) => String(value ?? "").trim();

const normalizeSkuList = (product) => {
  const candidates = [
    product?.sku,
    product?.selectedSku,
    product?.selectedSKU,
    product?.minSku,
    product?.defaultSku,
    product?.skus,
    product?.skuList,
    product?.sku_list,
    product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o,
    product?.ali_data?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o,
  ];

  const rows = [];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) rows.push(...candidate);
    else if (candidate && typeof candidate === "object") rows.push(candidate);
  }
  return rows;
};

const getWishlistProductId = (item) => {
  if (!item) return "";
  if (typeof item === "string" || typeof item === "number") return normalizeId(item);
  const productObject = item?.product && typeof item.product === "object" ? item.product : null;
  const candidate =
    item?.productId ||
    item?.product_id ||
    item?.productCountryId ||
    productObject?.productId ||
    productObject?.product_id ||
    productObject?.id ||
    productObject?._id ||
    item?.product ||
    "";
  return normalizeId(candidate);
};

const titleCase = (value) => {
  const source = String(value || "").trim();
  if (!source) return "";
  return source
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const normalizePath = (value) => {
  if (!Array.isArray(value)) return [];
  return uniqueList(
    value
      .map((entry) => titleCase(entry))
      .filter(Boolean)
  );
};

const candidateCategory = (product) =>
  uniqueList([
    product?.category?.name,
    product?.category_name,
    product?.categoryName,
    product?.mainCategory,
    product?.subcategory,
    product?.ae_item_base_info_dto?.first_level_category_name,
    product?.ae_item_base_info_dto?.second_level_category_name,
    product?.ae_item_base_info_dto?.category_name,
    typeof product?.category === "string" ? product.category : null,
  ]);

const candidateBrand = (product) =>
  uniqueList([
    product?.brand?.name,
    product?.brand_name,
    product?.brandName,
    product?.manufacturer,
    product?.store_name,
    product?.ae_item_base_info_dto?.brand_name,
    typeof product?.brand === "string" ? product.brand : null,
  ]);

const inferCategoryFromName = (name) => {
  const text = String(name || "");
  for (const entry of PRODUCT_TYPE_KEYWORDS) {
    if (entry.regex.test(text)) return entry.label;
  }
  return "Marketplace";
};

export const getMarketplaceProductMeta = (product) => {
  const id = String(product?.productId || product?.product_id || product?._id || product?.id || "").trim();
  const publicId = String(product?.publicId || product?.urlId || product?.productCountryId || product?._id || "").trim();
  const name = String(product?.name || product?.title || "Untitled product");

  const image =
    product?.image ||
    product?.thumbnail ||
    (Array.isArray(product?.images) && product.images.length ? product.images[0] : null) ||
    FALLBACK_IMAGE;

  const currencyRaw =
    product?.currency ||
    product?.currency_code ||
    product?.price_currency ||
    product?.priceCurrency ||
    "USD";
  const currency = /^[A-Z]{3}$/.test(String(currencyRaw).toUpperCase())
    ? String(currencyRaw).toUpperCase()
    : "USD";
  const skuRows = normalizeSkuList(product);
  const firstSku = skuRows[0] || null;

  const price = pickPreferredPrice(
    product?.sell_price,
    product?.offer_sale_price,
    product?.salePrice,
    product?.sellingPrice,
    product?.our_selling_price,
    product?.sku?.sell_price,
    product?.sku?.offer_sale_price,
    product?.selectedSku?.sell_price,
    product?.selectedSku?.offer_sale_price,
    firstSku?.sell_price,
    firstSku?.offer_sale_price,
    firstSku?.sku_price,
    product?.price,
    0
  );

  const oldPrice = toNumber(
    product?.originalPrice,
    product?.oldPrice,
    product?.compareAtPrice,
    product?.sku_price,
    product?.sku?.sku_price,
    product?.selectedSku?.sku_price,
    firstSku?.sku_price,
    product?.msrp
  );

  const discountPercent =
    price && oldPrice && oldPrice > price
      ? Math.round(((oldPrice - price) / oldPrice) * 100)
      : 0;

  const rating = toNumber(
    product?.ratingsAverage,
    product?.rating,
    product?.avgRating,
    product?.averageRating,
    product?.avg_evaluation_rating,
    product?.ae_item_base_info_dto?.avg_evaluation_rating
  );

  const reviewCount = toNumber(
    product?.ratingsQuantity,
    product?.reviewCount,
    product?.reviews_count,
    product?.ratings_total
  );

  const stock = toNumber(product?.availableStock, product?.stock, product?.sku?.sku_available_stock);

  const categoryCandidates = candidateCategory(product).map(titleCase);
  const categoryPath = normalizePath(product?.categoryPath);
  const brandCandidates = candidateBrand(product).map(titleCase);

  const category = categoryCandidates[0] || inferCategoryFromName(name);
  const categoryTrail = uniqueList([...categoryPath, ...categoryCandidates]).filter(Boolean);
  const brand = brandCandidates[0] || "Cherrfy";

  const createdAt = new Date(
    product?.createdAt ||
      product?.created_at ||
      product?.updatedAt ||
      product?.updated_at ||
      Date.now()
  ).getTime();

  return {
    id,
    publicId,
    name,
    image,
    currency,
    price: price ?? 0,
    oldPrice: oldPrice ?? null,
    discountPercent: discountPercent > 0 ? discountPercent : 0,
    rating: Number.isFinite(rating) ? Math.min(5, Math.max(0, Number(rating))) : null,
    reviewCount: Number.isFinite(reviewCount) ? Math.max(0, Number(reviewCount)) : 0,
    inStock: stock == null ? true : stock > 0,
    stock: stock == null ? null : stock,
    category,
    categoryPath,
    categoryTrail,
    brand,
    createdAt: Number.isFinite(createdAt) ? createdAt : Date.now(),
  };
};

const formatCompactCount = (n) => {
  const value = Number(n) || 0;
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}m`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return String(value);
};

const lineClamp2 = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const ActionIconButton = ({ label, onClick, children, active = false }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={
      "inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors " +
      (active
        ? "border-[#D2042D] bg-[#D2042D] text-white"
        : "border-white/70 bg-white/90 text-slate-700 hover:border-[#D2042D] hover:text-[#D2042D]")
    }
  >
    {children}
  </button>
);

const MarketplaceProductCard = memo(function MarketplaceProductCard({
  product,
  mode = "grid",
  meta: metaProp = null,
}) {
  const dispatch = useDispatch();
  const selectedCurrency = useSelector((state) => state?.userSettings?.currency) || "USD";
  const currencyPayload = useSelector((state) => state?.currency?.currency_list);
  const rates = useMemo(() => normalizeCurrencyRates(currencyPayload), [currencyPayload]);

  const meta = useMemo(() => metaProp || getMarketplaceProductMeta(product), [metaProp, product]);
  const productId = meta.id;
  const productHref = productId
    ? buildProductPath({ publicId: meta.publicId, productId, name: meta.name })
    : "#";
  const productRouteState = productId
    ? buildProductRouteState({ publicId: meta.publicId, productId, id: product?._id })
    : undefined;

  const money = (amount, from) =>
    fmtMoney(amount, {
      from: from || "USD",
      to: selectedCurrency || "USD",
      rates,
    });

  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [scrubIndex, setScrubIndex] = useState(0);
  const [displaySrc, setDisplaySrc] = useState(FALLBACK_IMAGE);
  const [incomingSrc, setIncomingSrc] = useState(null);
  const [isFading, setIsFading] = useState(false);
  const scrubRafRef = useRef(null);
  const scrubIndexRef = useRef(0);
  const fadeTimerRef = useRef(null);
  const wishlistItems = useSelector((state) => state?.wishlist?.items || []);

  useEffect(
    () => () => {
      if (scrubRafRef.current) cancelAnimationFrame(scrubRafRef.current);
      clearTimeout(fadeTimerRef.current);
    },
    []
  );

  useEffect(() => {
    scrubIndexRef.current = scrubIndex;
  }, [scrubIndex]);

  const isWishlisted = useMemo(() => {
    if (!productId) return false;
    return wishlistItems.some((item) => getWishlistProductId(item) === productId);
  }, [wishlistItems, productId]);

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!productId) return;
    if (isWishlisted) dispatch(removeFromWishlist(productId));
    else dispatch(addWishlistEntry(productId));
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickViewOpen(true);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!productId || !meta.inStock) return;
    setQuickViewOpen(true);
  };

  const isCompact = mode === "compact";
  const imageRatio = isCompact ? "aspect-[4/3]" : "aspect-[4/5]";
  const imageFit = isCompact ? "object-cover" : "object-contain";

  const imageList = useMemo(() => {
    const aliImages = String(product?.ae_multimedia_info_dto?.image_urls || "")
      .split(";")
      .map((src) => src.trim())
      .filter(Boolean);
    const altImagesRaw = product?.image_urls || product?.imageUrls || product?.gallery || product?.gallery_images;
    const altImages = Array.isArray(altImagesRaw)
      ? altImagesRaw
      : String(altImagesRaw || "")
        .split(";")
        .map((src) => src.trim())
        .filter(Boolean);
    const list = uniqueList([
      meta.image,
      ...(Array.isArray(product?.images) ? product.images : []),
      product?.image,
      product?.thumbnail,
      ...aliImages,
      ...altImages,
    ]).filter(Boolean);
    return list.length ? list : [FALLBACK_IMAGE];
  }, [meta.image, product]);

  const scrubFrames = useMemo(() => {
    if (imageList.length <= MAX_SCRUB_FRAMES) return imageList;
    const last = imageList.length - 1;
    const sampled = Array.from({ length: MAX_SCRUB_FRAMES }, (_, i) => {
      const idx = Math.floor((i * last) / (MAX_SCRUB_FRAMES - 1));
      return imageList[idx];
    });
    return sampled.filter(Boolean);
  }, [imageList]);

  useEffect(() => {
    const next = scrubFrames[0] || FALLBACK_IMAGE;
    setDisplaySrc(next);
    setIncomingSrc(null);
    setIsFading(false);
  }, [scrubFrames]);

  useEffect(() => {
    const next = scrubFrames[scrubIndex] || FALLBACK_IMAGE;
    if (next === displaySrc || next === incomingSrc) return;
    setIncomingSrc(next);
    setIsFading(true);
    clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      setDisplaySrc(next);
      setIncomingSrc(null);
      setIsFading(false);
    }, 160);
  }, [displaySrc, incomingSrc, scrubFrames, scrubIndex]);

  useEffect(() => {
    scrubIndexRef.current = 0;
    setScrubIndex(0);
  }, [scrubFrames.length]);

  const handleScrubMove = (e) => {
    if (!scrubFrames.length || scrubFrames.length === 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width || 1;
    const x = Math.min(Math.max(e.clientX - rect.left, 0), width);
    const next = Math.min(scrubFrames.length - 1, Math.floor((x / width) * scrubFrames.length));
    if (next === scrubIndexRef.current) return;
    if (scrubRafRef.current) cancelAnimationFrame(scrubRafRef.current);
    scrubRafRef.current = requestAnimationFrame(() => {
      scrubIndexRef.current = next;
      setScrubIndex(next);
    });
  };

  const handleScrubLeave = () => {
    if (scrubIndexRef.current === 0) return;
    scrubIndexRef.current = 0;
    setScrubIndex(0);
  };

  return (
    <>
      <article
        className={
          "group relative min-w-0 overflow-hidden rounded-xl sm:rounded-2xl border border-slate-200 bg-white transition-all duration-200 " +
          "hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg focus-within:ring-2 focus-within:ring-[#D2042D]/25 " +
          (!meta.inStock ? "opacity-80" : "")
        }
      >
        <div className="relative">
          <Link
            to={productHref}
            state={productRouteState}
            className={`block ${imageRatio} bg-white`}
            onMouseMove={handleScrubMove}
            onMouseLeave={handleScrubLeave}
          >
            <div className="relative h-full w-full p-2">
              <img
                src={displaySrc || FALLBACK_IMAGE}
                alt={meta.name}
                className={`absolute left-2 right-2 bottom-2 top-2.5 h-full w-full ${imageFit} transform-gpu transition-transform duration-500 ease-out will-change-transform group-hover:scale-105`}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = FALLBACK_IMAGE;
                }}
              />
              {incomingSrc ? (
                <img
                  src={incomingSrc}
                  alt={meta.name}
                  className={`absolute left-2 right-2 bottom-2 top-2.5 h-full w-full ${imageFit} transform-gpu transition-transform duration-500 ease-out will-change-transform group-hover:scale-105 transition-opacity duration-200 ${isFading ? "opacity-100" : "opacity-0"}`}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = FALLBACK_IMAGE;
                  }}
                />
              ) : null}
            </div>
          </Link>

          {scrubFrames.length > 1 ? (
            <div className="mt-2 flex items-center justify-center gap-1.5">
              {scrubFrames.map((_, idx) => (
                <span
                  key={`scrub-dot-${idx}`}
                  className={
                    "h-1.5 w-1.5 rounded-full transition-colors " +
                    (idx === scrubIndex ? "bg-slate-900" : "bg-slate-300")
                  }
                />
              ))}
            </div>
          ) : null}

          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between p-2">
            <div className="pointer-events-auto flex gap-1">
              {meta.discountPercent > 0 ? (
                <span className="inline-flex items-center rounded-full bg-[#D2042D] px-1.5 py-0.5 sm:px-2 sm:py-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-white">
                  -{meta.discountPercent}%
                </span>
              ) : null}
              {!meta.inStock ? (
                <span className="inline-flex items-center rounded-full bg-slate-900/90 px-1.5 py-0.5 sm:px-2 sm:py-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-white">
                  Sold Out
                </span>
              ) : null}
            </div>

            <div className="pointer-events-auto flex gap-1.5">
              <ActionIconButton
                label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                onClick={handleWishlist}
                active={isWishlisted}
              >
                <Heart size={14} fill={isWishlisted ? "currentColor" : "none"} />
              </ActionIconButton>

              <ActionIconButton label="Quick view" onClick={handleQuickView}>
                <Eye size={14} />
              </ActionIconButton>
            </div>
          </div>

          {meta.stock != null && meta.inStock ? (
            <div className="pointer-events-none absolute left-2 bottom-2 hidden sm:block">
              <span className="inline-flex items-center rounded-full bg-emerald-800 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                {meta.stock} in stock
              </span>
            </div>
          ) : null}
        </div>

        <div className="flex min-h-[110px] sm:min-h-[128px] flex-col gap-2 p-2.5 sm:p-3">
          <Link
            to={productHref}
            state={productRouteState}
            className="text-xs sm:text-sm font-semibold leading-4 sm:leading-5 text-slate-900 transition-colors hover:text-[#D2042D] focus-visible:outline-none focus-visible:underline"
            title={meta.name}
          >
            <span style={lineClamp2}>{meta.name}</span>
          </Link>

          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            {meta.rating ? (
              <>
                <Star size={13} className="text-amber-400" fill="currentColor" />
                <span className="font-semibold text-slate-700">{meta.rating.toFixed(1)}</span>
              </>
            ) : (
              <span className="text-slate-500">No reviews yet</span>
            )}
            {meta.reviewCount > 0 ? <span>({formatCompactCount(meta.reviewCount)} reviews)</span> : null}
          </div>

          <div className="mt-auto flex flex-col items-stretch gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm sm:text-base font-bold text-[#D2042D]">
                {meta.price > 0 ? money(meta.price, meta.currency) : "See available options"}
              </p>
              {meta.oldPrice && meta.oldPrice > meta.price ? (
                <p className="truncate text-xs text-slate-500 line-through">
                  {money(meta.oldPrice, meta.currency)}
                </p>
              ) : null}
            </div>

            <button
              type="button"
              onClick={handleAddToCart}
              disabled={!meta.inStock}
              className={
                "inline-flex h-8 sm:h-9 items-center justify-center gap-1.5 rounded-xl px-2.5 sm:px-3 text-[10px] sm:text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D2042D]/30 " +
                (meta.inStock
                  ? "bg-slate-900 text-white hover:bg-[#D2042D]"
                  : "cursor-not-allowed bg-slate-200 text-slate-500")
              }
            >
              <ShoppingCart size={14} />
              <span>Choose options</span>
            </button>
          </div>
        </div>
      </article>

      {quickViewOpen ? (
        <QuickViewModal
          productData={{
            id: meta.id,
            name: meta.name,
            price: meta.price,
            imageUrl: meta.image,
            shortDescription: product?.shortDescription || product?.description || "",
            availableStock:
              typeof product?.availableStock === "number"
                ? product.availableStock
                : typeof product?.stock === "number"
                  ? product.stock
                  : 0,
            currency: meta.currency,
          }}
          onClose={() => setQuickViewOpen(false)}
        />
      ) : null}
    </>
  );
});

export const MarketplaceProductCardSkeleton = ({ mode = "grid" }) => {
  const imageRatio = mode === "compact" ? "aspect-[4/3]" : "aspect-[4/5]";
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className={`${imageRatio} animate-pulse bg-slate-200`} />
      <div className="space-y-2 p-3">
        <div className="h-4 w-full animate-pulse rounded bg-slate-200" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-slate-200" />
        <div className="h-3 w-2/5 animate-pulse rounded bg-slate-200" />
        <div className="mt-2 h-8 w-full animate-pulse rounded-xl bg-slate-200" />
      </div>
    </div>
  );
};

export default MarketplaceProductCard;
